import { type ReactNode } from 'react';
import styled, { css } from 'styled-components';

import { rubikTypographyStyles } from '../../../../shared/ui';

export type RaffleRuleTextPart = {
  text: string;
  weight?: 'regular' | 'medium' | 'semibold' | 'bold';
};

export type RaffleRuleStep = {
  descriptionAlign?: 'left' | 'right';
  descriptionMaxWidthPx?: number;
  descriptionParts: ReadonlyArray<RaffleRuleTextPart>;
  imageAlign?: 'left' | 'right';
  imageAlt?: string;
  imageMaxWidthPx?: number;
  imageSrc?: string;
  number: string;
};

export type RaffleRulesSectionProps = {
  className?: string;
  note?: string;
  steps: ReadonlyArray<RaffleRuleStep>;
  titleAccent?: string;
  title: string;
};

const Section = styled.section.attrs({
  'data-ui': 'raffle-rules-section',
})`
  ${rubikTypographyStyles};
  display: grid;
  gap: ${({ theme }) => theme.scale.px(28)};
  margin-top: ${({ theme }) => theme.scale.px(28)};
`;

const Title = styled.h2`
  margin: 0;
  text-align: center;
  display: grid;
  justify-items: center;
  font-size: ${({ theme }) => theme.scale.px(36)};
  font-weight: 600;
  line-height: 1.1;
`;

const TitleLine = styled.span`
  display: block;
`;

const TitleAccent = styled(TitleLine)`
  color: ${({ theme }) => theme.colors.brand.primary};
`;

const Steps = styled.div`
  display: grid;
  gap: ${({ theme }) => theme.scale.px(28)};
`;

const StepNumber = styled.p<{ $align: 'left' | 'right' }>`
  margin: 0;
  text-align: center;
  justify-self: ${({ $align }) => ($align === 'right' ? 'end' : 'start')};
  font-size: ${({ theme }) => theme.scale.px(56)};
  font-weight: 500;
  line-height: 1;
  color: ${({ theme }) => theme.colors.brand.primary};
`;

const Step = styled.article<{ $align: 'left' | 'right' }>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.scale.px(10)};

  ${({ $align }) =>
    $align === 'left'
      ? css`
          flex-direction: row-reverse;
        `
      : css`
          flex-direction: row;
        `}
`;

const StepCopy = styled.div<{ $maxWidthPx?: number }>`
  display: grid;
  gap: ${({ theme }) => theme.scale.px(2)};
  width: ${({ $maxWidthPx, theme }) => theme.scale.px($maxWidthPx ?? 212)};
  max-width: 100%;
`;

const StepDescription = styled.p<{
  $maxWidthPx?: number;
  $textAlign: 'left' | 'right';
}>`
  margin: 0;
  text-align: ${({ $textAlign }) => $textAlign};
  font-size: ${({ theme }) => theme.scale.px(18)};
  font-weight: 400;
  line-height: 1.2;
`;

const StepDescriptionPart = styled.span<{
  $weight: NonNullable<RaffleRuleTextPart['weight']>;
}>`
  ${({ $weight }) => {
    switch ($weight) {
      case 'bold':
        return 'font-weight: 700;';
      case 'semibold':
        return 'font-weight: 600;';
      case 'medium':
        return 'font-weight: 500;';
      default:
        return 'font-weight: 400;';
    }
  }}
`;

const StepIllustration = styled.img<{ $maxWidthPx?: number }>`
  width: ${({ $maxWidthPx, theme }) => theme.scale.px($maxWidthPx ?? 120)};
  max-width: 100%;
  height: auto;
  display: block;
  object-fit: contain;
  flex-shrink: 0;
`;

const Note = styled.p`
  margin: 0;
  max-width: ${({ theme }) => theme.scale.px(311)};
  justify-self: center;
  text-align: center;
  font-size: ${({ theme }) => theme.scale.px(12)};
  line-height: 1.15;
  color: ${({ theme }) => theme.colors.text.subdued};
`;

function renderDescriptionParts(step: RaffleRuleStep): ReactNode {
  return step.descriptionParts.map((part, index) => (
    <StepDescriptionPart
      key={`${step.number}-${index}`}
      $weight={part.weight ?? 'regular'}
    >
      {part.text}
    </StepDescriptionPart>
  ));
}

export function RaffleRulesSection({
  className,
  note,
  steps,
  titleAccent,
  title,
}: RaffleRulesSectionProps) {
  const accessibleTitle = titleAccent ? `${title} ${titleAccent}` : title;

  return (
    <Section className={className}>
      <Title aria-label={accessibleTitle}>
        <TitleLine>{title}</TitleLine>
        {titleAccent ? <TitleAccent>{titleAccent}</TitleAccent> : null}
      </Title>
      <Steps>
        {steps.map(step => {
          const align = step.imageAlign ?? 'right';
          const textAlign =
            step.descriptionAlign ?? (align === 'right' ? 'right' : 'left');
          const descriptionLabel = step.descriptionParts
            .map(part => part.text)
            .join('');

          return (
            <Step key={step.number} $align={align}>
              <StepCopy $maxWidthPx={step.descriptionMaxWidthPx}>
                <StepNumber $align={align}>{step.number}</StepNumber>
                <StepDescription
                  $maxWidthPx={step.descriptionMaxWidthPx}
                  $textAlign={textAlign}
                  aria-label={descriptionLabel}
                >
                  {renderDescriptionParts(step)}
                </StepDescription>
              </StepCopy>
              {step.imageSrc ? (
                <StepIllustration
                  $maxWidthPx={step.imageMaxWidthPx}
                  alt={step.imageAlt ?? ''}
                  src={step.imageSrc}
                />
              ) : null}
            </Step>
          );
        })}
      </Steps>
      {note ? <Note>{note}</Note> : null}
    </Section>
  );
}
